import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { translateOptions } from "../../helpers/optionLabels";
import { useTheme } from "@mui/material";
import Typography from "components/basic/typography";
import PowerChargerImage from "resources/images/PowerChargerImage.png";
import { useNavigate } from "react-router-dom";
import AutoComplete from "components/basic/autoComplete";
import { useSnack } from "hooks/useSnack";
import { ChargerEstimatorToolsPagesProps } from "pages/chargerEstimator";
import UploadFile from "components/basic/uploadFile";
import { validateFile } from "pages/formTools";
import FormPageLayout from "components/basic/formPageLayout";

const PowerAccess: React.FC<ChargerEstimatorToolsPagesProps> = ({
  chargerEstimatorData,
  setChargerEstimatorData,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("estimator");
  const navigate = useNavigate();
  const showSnack = useSnack();

  // A photo preview pushes whatever follows it out of the scroll region, so the
  // second upload can end up off screen with no sign it exists. Bring the end of
  // the form back into view once the preview has taken up its space.
  const formEndRef = useRef<HTMLDivElement>(null);
  const revealFormEnd = useCallback(() => {
    formEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, []);

  const panelLocationOptions = [
    { id: 1, label: "Main Floor" },
    { id: 2, label: "Basement" },
    { id: 3, label: "Garage" },
  ];
  const serviceSizeOptions = [
    { id: 1, label: "100A" },
    { id: 2, label: "200A" },
    { id: 3, label: "400A" },
  ];
  const wireLengthOptions = [
    { id: 1, label: "10 FT" },
    { id: 2, label: "20 FT" },
    { id: 3, label: "30 FT" },
    { id: 4, label: "40 FT" },
    { id: 5, label: "50 FT" },
  ];

  const panelLocation = chargerEstimatorData.electricalPanelLocation
    ? panelLocationOptions.find(
        (opt) => opt.label === chargerEstimatorData.electricalPanelLocation,
      ) || null
    : null;
  const serviceSize = chargerEstimatorData.mainServiceSize
    ? serviceSizeOptions.find(
        (opt) => opt.label === chargerEstimatorData.mainServiceSize,
      ) || null
    : null;
  const wireLength = chargerEstimatorData.wireLength
    ? wireLengthOptions.find(
        (opt) => opt.label === chargerEstimatorData.wireLength,
      ) || null
    : null;

  const [errors, setErrors] = useState<{ [key: string]: string | null }>({
    mainBreakerPhoto: null,
    electricalPanelPhoto: null,
  });

  const handleNext = () => {
    const mainBreakerPhotoError = validateFile({
      name: t("power.breaker_photo"),
      file: chargerEstimatorData.mainBreakerPhoto,
      required: true,
      allowedTypes: ["pdf", "image/*"],
      errorMessage: t("power.invalid_file"),
    });
    const electricalPanelPhotoError = validateFile({
      name: t("power.panel_photo"),
      file: chargerEstimatorData.electricalPanelPhoto,
      required: true,
      allowedTypes: ["pdf", "image/*"],
      errorMessage: t("power.invalid_file"),
    });

    setErrors({
      mainBreakerPhoto: mainBreakerPhotoError,
      electricalPanelPhoto: electricalPanelPhotoError,
    });

    if (!chargerEstimatorData.electricalPanelLocation) {
      showSnack(t("power.panel_required"), "error");
      return;
    }
    if (!chargerEstimatorData.mainServiceSize) {
      showSnack(t("power.size_required"), "error");
      return;
    }
    if (!chargerEstimatorData.wireLength) {
      showSnack(t("power.wire_required"), "error");
      return;
    }
    if (mainBreakerPhotoError) {
      showSnack(t("power.breaker_photo_required"), "error");
      return;
    }
    if (electricalPanelPhotoError) {
      showSnack(t("power.panel_photo_required"), "error");
      return;
    }
    navigate("?currentPage=preferences");
  };

  return (
    <FormPageLayout
      imageSrc={PowerChargerImage}
      imageAlt={t("power.image_alt")}
      imagePosition="right"
      bodyScroll
      title={
        <Typography
          style={{ fontWeight: "bold", fontSize: "2.5em" }}
          variant="h2"
          align="inherit"
        >
          {t("power.title")}
        </Typography>
      }
      backAction={{
        label: t("nav.back"),
        color: "primary",
        onClick: () => navigate("?currentPage=homeSetup"),
      }}
      primaryAction={{
        label: t("nav.next"),
        color: "warning",
        onClick: handleNext,
      }}
    >
      <div>
        <Typography
          style={{ marginBottom: theme.spacing(1) }}
          variant="body2"
          align="inherit"
        >
          {t("power.panel_location")}
        </Typography>

        <AutoComplete
          size="tiny"
          textAlign="center"
          value={panelLocation?.id || null}
          label={t("nav.please_select")}
          options={translateOptions(t, panelLocationOptions)}
          onChange={(value) =>
            setChargerEstimatorData({
              ...chargerEstimatorData,
              electricalPanelLocation:
                panelLocationOptions.find((o) => o.id === value?.id)?.label ||
                "",
            })
          }
        />
      </div>

      <div>
        <Typography
          style={{ marginBottom: theme.spacing(1) }}
          variant="body2"
          align="inherit"
        >
          {t("power.service_size")}
        </Typography>

        <AutoComplete
          size="tiny"
          textAlign="center"
          value={serviceSize?.id || null}
          label={t("nav.please_select")}
          options={translateOptions(t, serviceSizeOptions)}
          onChange={(value) =>
            setChargerEstimatorData({
              ...chargerEstimatorData,
              mainServiceSize:
                serviceSizeOptions.find((o) => o.id === value?.id)?.label || "",
            })
          }
        />
      </div>

      <div>
        <Typography
          style={{ marginBottom: theme.spacing(1) }}
          variant="body2"
          align="inherit"
        >
          {t("power.wire_length")}
        </Typography>

        <AutoComplete
          size="tiny"
          textAlign="center"
          value={wireLength?.id || null}
          label={t("nav.please_select")}
          options={translateOptions(t, wireLengthOptions)}
          onChange={(value) =>
            setChargerEstimatorData({
              ...chargerEstimatorData,
              wireLength:
                wireLengthOptions.find((o) => o.id === value?.id)?.label || "",
            })
          }
        />
      </div>

      <div>
        <Typography
          style={{ marginBottom: theme.spacing(1) }}
          variant="body2"
          align="inherit"
        >
          {t("power.panel_photo")}
        </Typography>

        <UploadFile
          fullWidth={true}
          file={chargerEstimatorData.electricalPanelPhoto}
          onPreviewLoad={revealFormEnd}
          onChange={(files) => {
            setChargerEstimatorData({
              ...chargerEstimatorData,
              electricalPanelPhoto: files,
            });
          }}
          {...(errors?.electricalPanelPhoto
            ? { error: errors.electricalPanelPhoto }
            : {})}
        />
      </div>

      <div>
        <Typography
          style={{ marginBottom: theme.spacing(1) }}
          variant="body2"
          align="inherit"
        >
          {t("power.breaker_photo")}
        </Typography>

        <UploadFile
          fullWidth={true}
          file={chargerEstimatorData.mainBreakerPhoto}
          onPreviewLoad={revealFormEnd}
          onChange={(files) => {
            setChargerEstimatorData({
              ...chargerEstimatorData,
              mainBreakerPhoto: files,
            });
          }}
          {...(errors?.mainBreakerPhoto
            ? { error: errors.mainBreakerPhoto }
            : {})}
        />
      </div>

      <div ref={formEndRef} />
    </FormPageLayout>
  );
};

export default PowerAccess;
